import React, {useCallback, useEffect, useState} from 'react';
import {formatDate as formatApiDate} from '../utils/dateTime';
import {useNavigate, useParams} from 'react-router-dom';
import createAxiosInstance from "../support/AxiosSupport";
import AuthImage from "../components/AuthImage";
import Spinner from "../components/Spinner";
import {ChevronDownIcon, DisposalIcon, PlusIcon, SearchIcon, TrashIcon, XIcon} from "../components/icons";
import {hasPermission, useWardrobe} from "../context/WardrobeContext";
import {useDocumentTitle} from '../hooks/useDocumentTitle';

interface ClothingItemListDto {
    id: string;
    name: string;
    categoryName: string | null;
    brandName: string | null;
    sizeName: string | null;
    conditionName: string | null;
    colorNames: string | null;
    purchaseDate: string | null;
    firstPhotoUrl: string | null;
    thrownAway: boolean | null;
    inTrashBin?: boolean | null;
}

interface ClothingListDetail {
    id: string;
    name: string;
    description: string | null;
    items: ClothingItemListDto[];
}

interface PageResponse {
    content: ClothingItemListDto[];
}

const ClothingListDetailPage: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const {selectedOwnerId, isOwnWardrobe, permissions} = useWardrobe();
    const canManageListItems = isOwnWardrobe || hasPermission(permissions, 'MANAGE_LIST_ITEMS');
    const canViewThrownAway = isOwnWardrobe || hasPermission(permissions, 'VIEW_THROWN_AWAY');
    const [list, setList] = useState<ClothingListDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showThrownAway, setShowThrownAway] = useState(false);
    const [searchResults, setSearchResults] = useState<ClothingItemListDto[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);

    const fetchList = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setError(null);
        try {
            const api = await createAxiosInstance();
            const params = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
            const res = await api.get<ClothingListDetail>(`/lists/${id}`, {params});
            setList(res.data);
        } catch (err) {
            console.error('Error fetching list:', err);
            setError('Не удалось загрузить список');
            setList(null);
        } finally {
            setLoading(false);
        }
    }, [id, selectedOwnerId]);

    useEffect(() => {
        if (id) fetchList();
    }, [id, fetchList]);

    // Пока список не загружен — заголовок раздела («Списки»), потом имя списка.
    useDocumentTitle(list?.name);

    const handleAddItem = async (itemId: string) => {
        if (!id) return;
        try {
            const api = await createAxiosInstance();
            const params = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
            await api.post(`/lists/${id}/items/${itemId}`, {}, {params});
            fetchList();
            setAddModalOpen(false);
            setSearchQuery('');
            setSearchResults([]);
        } catch (err) {
            console.error('Error adding item to list:', err);
        }
    };

    const handleRemoveItem = async (itemId: string) => {
        if (!id) return;
        try {
            const api = await createAxiosInstance();
            const params = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
            await api.delete(`/lists/${id}/items/${itemId}`, {params});
            fetchList();
        } catch (err) {
            console.error('Error removing item from list:', err);
        }
    };

    const searchItems = useCallback(async () => {
        setSearchLoading(true);
        try {
            const api = await createAxiosInstance();
            const hasSearch = !!searchQuery.trim();
            const res = await api.get<PageResponse>('/clothing/items', {
                params: {
                    page: 0,
                    size: hasSearch ? 50 : 200,
                    sortBy: 'name',
                    sortDir: 'asc',
                    includeThrownAway: false,
                    ...(selectedOwnerId ? {ownerId: selectedOwnerId} : {}),
                    ...(hasSearch ? {search: searchQuery.trim()} : {})
                }
            });
            const itemIdsInList = new Set((list?.items ?? []).map((i) => i.id));
            setSearchResults((res.data?.content ?? []).filter((item) => !itemIdsInList.has(item.id)));
        } catch (err) {
            console.error('Error searching items:', err);
            setSearchResults([]);
        } finally {
            setSearchLoading(false);
        }
    }, [searchQuery, list?.items, selectedOwnerId]);

    useEffect(() => {
        if (!addModalOpen) return;
        const t = setTimeout(searchItems, 300);
        return () => clearTimeout(t);
    }, [addModalOpen, searchQuery, searchItems]);

    const formatDate = (dateStr: string | null) => formatApiDate(dateStr);

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate('/lists')}
                    className="flex items-center gap-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 text-sm"
                >
                    <ChevronDownIcon className="w-5 h-5 rotate-90"/>
                    Назад
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <Spinner/>
                </div>
            ) : error ? (
                <div className="p-4 text-center text-red-600 dark:text-red-400">{error}</div>
            ) : list ? (
                <div className="space-y-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{list.name}</h1>
                        {list.description && (
                            <p className="text-gray-600 dark:text-gray-400 mt-1">{list.description}</p>
                        )}
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Вещи в списке</h2>
                        <div className="flex items-center gap-4">
                            {canViewThrownAway && (
                            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 dark:text-gray-400">
                                <input
                                    type="checkbox"
                                    checked={showThrownAway}
                                    onChange={(e) => setShowThrownAway(e.target.checked)}
                                    className="rounded"
                                />
                                Показать выброшенные
                            </label>
                            )}
                            {canManageListItems && (
                            <button
                                onClick={() => setAddModalOpen(true)}
                                className="btn-primary flex items-center gap-2"
                            >
                                <PlusIcon className="w-5 h-5"/>
                                Добавить
                            </button>
                            )}
                        </div>
                    </div>

                    {(() => {
                        const visibleItems = showThrownAway ? list.items : list.items.filter((i) => !i.thrownAway);
                        if (visibleItems.length === 0) return (
                            <div className="text-center py-12 text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                                {list.items.length === 0
                                    ? 'Список пуст. Нажмите «Добавить», чтобы добавить вещи.'
                                    : 'Нет вещей для отображения. Включите «Показать выброшенные».'}
                            </div>
                        );
                        return (
                        <div key="items-grid" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {visibleItems.map((item) => (
                                <div
                                    key={item.id}
                                    className={`flex gap-3 p-3 rounded-lg border bg-white dark:bg-gray-800 ${
                                        item.thrownAway ? 'border-amber-300 dark:border-amber-600 opacity-75' : (item.inTrashBin ? 'border-gray-400 dark:border-gray-500 opacity-90' : 'border-gray-200 dark:border-gray-600')
                                    }`}
                                >
                                    <div
                                        className="w-16 h-16 flex-shrink-0 rounded overflow-hidden bg-gray-100 dark:bg-gray-700 cursor-pointer relative"
                                        onClick={() => navigate(`/clothing/${item.id}`)}
                                    >
                                        {item.firstPhotoUrl ? (
                                            <AuthImage imageKey={item.firstPhotoUrl} alt={item.name} variant="thumbnail" className="w-full h-full object-cover"/>
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">—</div>
                                        )}
                                        {item.thrownAway && (
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                                                <DisposalIcon className="w-6 h-6 text-white"/>
                                            </div>
                                        )}
                                        {item.inTrashBin && !item.thrownAway && (
                                            <div className="absolute top-1 right-1 px-1.5 py-0.5 rounded bg-gray-700/80 text-white text-xs flex items-center gap-1">
                                                <TrashIcon className="w-3 h-3"/> Ждёт передачи
                                            </div>
                                        )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => navigate(`/clothing/${item.id}`)}
                                                className="font-medium text-gray-900 dark:text-gray-100 hover:underline truncate block text-left"
                                            >
                                                {item.name}
                                            </button>
                                            {item.thrownAway && (
                                                <span className="flex-shrink-0 px-1.5 py-0.5 text-xs rounded bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200">
                                                    Выброшено
                                                </span>
                                            )}
                                            {item.inTrashBin && !item.thrownAway && (
                                                <span className="flex-shrink-0 px-1.5 py-0.5 text-xs rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300">
                                                    Ждёт передачи
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                            {[item.categoryName, item.brandName].filter(Boolean).join(' · ') || '—'}
                                        </p>
                                    </div>
                                    {canManageListItems && (
                                    <button
                                        onClick={() => handleRemoveItem(item.id)}
                                        className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 flex-shrink-0"
                                        title="Убрать из списка"
                                    >
                                        <TrashIcon className="w-5 h-5"/>
                                    </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        );
                    })()}
                </div>
            ) : null}

            {addModalOpen && (
                <div
                    className="fw-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                    onClick={() => setAddModalOpen(false)}
                >
                    <div
                        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-600">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Добавить вещь</h2>
                            <button
                                onClick={() => setAddModalOpen(false)}
                                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <XIcon className="w-5 h-5"/>
                            </button>
                        </div>
                        <div className="p-4 border-b border-gray-200 dark:border-gray-600">
                            <div className="relative">
                                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"/>
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="input-field w-full pl-10"
                                    placeholder="Поиск по названию, категории, бренду..."
                                    autoFocus
                                />
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4">
                            {searchLoading ? (
                                <div className="flex justify-center py-8">
                                    <Spinner/>
                                </div>
                            ) : searchResults.length === 0 ? (
                                <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                                    {searchQuery.trim() ? 'Ничего не найдено' : 'Нет доступных вещей'}
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    {searchResults.map((item) => (
                                        <div
                                            key={item.id}
                                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                                            onClick={() => handleAddItem(item.id)}
                                        >
                                            <div className="w-10 h-10 flex-shrink-0 rounded overflow-hidden bg-gray-200 dark:bg-gray-600">
                                                {item.firstPhotoUrl ? (
                                                    <AuthImage imageKey={item.firstPhotoUrl} alt={item.name} variant="thumbnail" className="w-full h-full object-cover"/>
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">—</div>
                                                )}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="font-medium text-gray-900 dark:text-gray-100 truncate">{item.name}</p>
                                                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                                    {[item.categoryName, item.brandName].filter(Boolean).join(' · ') || '—'}
                                                </p>
                                            </div>
                                            <PlusIcon className="w-5 h-5 text-gray-400 flex-shrink-0"/>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClothingListDetailPage;
